import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { Loader2, Folder, FileText, CheckCircle2, ShieldAlert, Clock, ExternalLink, PlayCircle, PauseCircle, Ban, XCircle, Copy, IndianRupee, Info, ChevronDown, Send, Mail, RotateCcw, TrendingUp } from 'lucide-react';
import { IdeasFilterTable } from '../components/IdeasFilterTable.tsx';
import { AmountDisplay } from '../components/AmountDisplay.tsx';
import { CountCardHint } from '../components/CountCardHint.tsx';
import { formatIndianCurrency } from '../utils/currency.ts';
import { useSessionState } from '../hooks/useSessionState.ts';
export function CommitteeLeaderDashboard({ showFuturePotentialByEc = true }: { showFuturePotentialByEc?: boolean } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedHeadIds, setSelectedHeadIds] = useSessionState<string[]>('leader_selectedHeadIds', []);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useSessionState<string[]>('leader_selectedAssigneeIds', []);
  const [isHeadDropdownOpen, setIsHeadDropdownOpen] = useState(false);
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  const [headSearch, setHeadSearch] = useState('');
  const headDropdownRef = useRef<HTMLDivElement>(null);
  const memberDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (headDropdownRef.current && !headDropdownRef.current.contains(event.target as Node)) {
        setIsHeadDropdownOpen(false);
      }
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target as Node)) {
        setIsMemberDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: userRoleMappings } = useQuery({
    queryKey: ['userRoleMappings'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/user-role-mappings');
      return res.data.data || [];
    }
  });

  const currentUserId = localStorage.getItem('userId');
  const currentUser = userRoleMappings?.find((u: any) => u.id === currentUserId);
  const leaderDepartments = currentUser?.managedDepartments || [];

  const committeeHeads = userRoleMappings?.filter((u: any) =>
    u.role === 'COMMITTEE_HEAD' &&
    (leaderDepartments.length === 0 || u.managedDepartments?.some((dept: string) => leaderDepartments.includes(dept)))
  ) || [];

  const allCommitteeMembers = userRoleMappings?.filter((u: any) =>
    u.role === 'COMMITTEE_MEMBER' &&
    (leaderDepartments.length === 0 || u.managedDepartments?.some((dept: string) => leaderDepartments.includes(dept)))
  ) || [];
  const filteredMembers = selectedHeadIds.length > 0
    ? allCommitteeMembers.filter((m: any) => selectedHeadIds.includes(m.reportsToId))
    : allCommitteeMembers;

  const searchedHeads = committeeHeads.filter((h: any) => h.name.toLowerCase().includes(headSearch.toLowerCase()));
  const selectedHeadsList = committeeHeads.filter((h: any) => selectedHeadIds.includes(h.id));

  // Cascading filter logic matching Admin's approach:
  // When heads are selected → use their managedDepartments as department filter
  // When specific members are selected → use assigneeId filter
  let effectiveDepartmentIds: string | undefined = undefined;
  let finalAssigneeIds: string[] = [];

  if (selectedAssigneeIds.length > 0) {
    // Specific member(s) selected → filter by assignee
    finalAssigneeIds = selectedAssigneeIds;
  } else if (selectedHeadIds.length > 0) {
    // Head(s) selected, no specific member → filter by heads' managed departments
    const headDepts = Array.from(new Set(selectedHeadsList.flatMap((h: any) => h.managedDepartments || []))) as string[];
    if (headDepts.length > 0) {
      effectiveDepartmentIds = headDepts.join('|');
    } else {
      // Fallback: use member IDs if heads have no departments
      finalAssigneeIds = filteredMembers.map((m: any) => m.id);
    }
  }

  // Reset page to 1 when top-level filters change
  const resetPage = () => {
    setSearchParams(params => { params.delete('page'); return params; });
  };

  const { data: globalStats, isLoading: statsLoading } = useQuery({
    queryKey: ['adminStats', finalAssigneeIds.join(','), effectiveDepartmentIds],
    queryFn: async () => {
      const res = await apiClient.get('/admin/stats', {
        params: {
          assigneeId: finalAssigneeIds.length > 0 ? finalAssigneeIds.join(',') : undefined,
          department: effectiveDepartmentIds || undefined
        }
      });
      return res.data.data;
    },
  });

  return (
    <Layout title="Committee Leader Portal" >
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-lg font-bold text-slate-800">DOM-680 Dashboard</h2>
          <p className="text-slate-500 text-xs mt-0.5">Review, prioritize and moderate idea workflow</p>
        </div>
      </div>

      {/* Cascading Filters Card */}
      <div className="bg-white p-3 sm:p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row flex-wrap gap-3 sm:gap-4 mb-4" >
        {/* Committee Head Dropdown */}
        < div className="flex flex-col flex-1 min-w-[150px] max-w-[240px]" ref={headDropdownRef} >
          <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
            <ShieldAlert className="w-3 h-3 text-emerald-500" />
            Committee Heads
          </label>
          <div className={`relative ${isHeadDropdownOpen ? 'z-50' : 'z-20'}`}>
            <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-2 text-slate-400 pointer-events-none" />
            <button
              onClick={() => setIsHeadDropdownOpen(!isHeadDropdownOpen)}
              className="w-full border border-slate-200 rounded-lg px-3 pr-8 py-1.5 text-xs bg-slate-50 hover:bg-slate-100 transition-colors focus:bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-left shadow-sm"
            >
              <span className="truncate font-medium text-slate-700">
                {selectedHeadIds.length === 0
                  ? 'All Heads'
                  : selectedHeadIds.length === 1
                    ? committeeHeads?.find((m: any) => m.id === selectedHeadIds[0])?.name || 'Selected (1)'
                    : `${selectedHeadIds.length} Selected`}
              </span>
            </button>
            {isHeadDropdownOpen && (
              <div className="absolute top-full left-0 mt-1.5 w-full sm:w-[280px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                <div className="p-2 border-b border-slate-100 bg-slate-50 sticky top-0 z-10">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search heads..."
                      className="w-full text-xs pl-3 pr-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                      value={headSearch}
                      onChange={(e) => setHeadSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                <div className="overflow-y-auto max-h-60 flex-1">
                  <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                    <input
                      type="checkbox"
                      className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      checked={searchedHeads.length > 0 && selectedHeadIds.length === searchedHeads.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedHeadIds(searchedHeads.map((h: any) => h.id));
                        } else {
                          setSelectedHeadIds([]);
                        }
                        setSelectedAssigneeIds([]);
                        resetPage();
                      }}
                    />
                    <span className="text-sm text-slate-800 font-bold">Select All</span>
                  </label>
                  {searchedHeads?.map((m: any) => (
                    <label key={m.id} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                      <input
                        type="checkbox"
                        className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 shrink-0"
                        checked={selectedHeadIds.includes(m.id)}
                        onChange={(e) => {
                          setSelectedHeadIds(prev =>
                            e.target.checked ? [...prev, m.id] : prev.filter(id => id !== m.id)
                          );
                          setSelectedAssigneeIds([]);
                          resetPage();
                        }}
                      />
                      <div className="flex flex-col overflow-hidden">
                        <span className="truncate leading-tight text-slate-700 font-medium group-hover:text-emerald-700 transition-colors">{m.name}</span>
                        {m.managedDepartments?.length > 0 && (
                          <span className="text-slate-500 text-[10px] uppercase font-bold leading-tight truncate mt-0.5">{m.managedDepartments.join(' | ')}</span>
                        )}
                      </div>
                    </label>
                  ))}
                  {searchedHeads.length === 0 && (
                    <div className="px-4 py-4 text-sm text-slate-500 text-center font-medium">No heads found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div >

        {/* Committee Member Dropdown */}
        < div className="flex flex-col flex-1 min-w-[150px] max-w-[240px]" ref={memberDropdownRef} >
          <label className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
            <FileText className="w-3 h-3 text-indigo-500" />
            Committee Member
          </label>
          <div className={`relative ${isMemberDropdownOpen ? 'z-50' : 'z-20'}`}>
            <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 top-2 text-slate-400 pointer-events-none" />
            <button
              onClick={() => setIsMemberDropdownOpen(!isMemberDropdownOpen)}
              className="w-full border border-slate-200 rounded-lg px-3 pr-8 py-1.5 text-xs bg-slate-50 hover:bg-slate-100 transition-colors focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-left shadow-sm"
            >
              <span className="truncate font-medium text-slate-700">
                {selectedAssigneeIds.length === 0
                  ? 'All Members'
                  : selectedAssigneeIds.length === 1
                    ? filteredMembers?.find((m: any) => m.id === selectedAssigneeIds[0])?.name || 'Selected (1)'
                    : `${selectedAssigneeIds.length} Selected`}
              </span>
            </button>
            {isMemberDropdownOpen && (
              <div className="absolute top-full left-0 mt-1.5 w-full sm:w-[280px] bg-white border border-slate-200 rounded-lg shadow-xl z-50 flex flex-col overflow-hidden">
                <div className="overflow-y-auto max-h-60 flex-1">
                  <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100 bg-slate-50/50">
                    <input
                      type="checkbox"
                      className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      checked={selectedAssigneeIds.length === 0}
                      onChange={() => { setSelectedAssigneeIds([]); resetPage(); }}
                    />
                    <span className="text-sm text-slate-800 font-bold">All Members (Clear)</span>
                  </label>
                  <label className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group border-b border-slate-100">
                    <input
                      type="checkbox"
                      className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      checked={filteredMembers && filteredMembers.length > 0 && selectedAssigneeIds.length === filteredMembers.length}
                      onChange={(e) => {
                        if (e.target.checked && filteredMembers) {
                          setSelectedAssigneeIds(filteredMembers.map((m: any) => m.id));
                        } else {
                          setSelectedAssigneeIds([]);
                        }
                        resetPage();
                      }}
                    />
                    <span className="text-sm text-slate-800 font-bold">Select All</span>
                  </label>
                  {filteredMembers?.length === 0 ? (
                    <div className="px-4 py-4 text-sm text-slate-500 text-center font-medium">No members found</div>
                  ) : (
                    filteredMembers?.map((m: any) => (
                      <label key={m.id} className="flex items-start px-4 py-2 hover:bg-slate-50 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="mt-0.5 mr-3 cursor-pointer rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 shrink-0"
                          checked={selectedAssigneeIds.includes(m.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAssigneeIds(prev => [...prev, m.id]);
                            } else {
                              setSelectedAssigneeIds(prev => prev.filter(id => id !== m.id));
                            }
                            resetPage();
                          }}
                        />
                        <div className="flex flex-col overflow-hidden">
                          <span className="truncate leading-tight text-slate-700 font-medium group-hover:text-indigo-700 transition-colors">{m.name}</span>
                          {(() => {
                            const depts = m.managedDepartments?.length > 0
                              ? m.managedDepartments
                              : (() => {
                                const parent = committeeHeads?.find((h: any) => h.id === m.reportsToId);
                                return parent?.managedDepartments || [];
                              })();
                            return depts?.length > 0 ? (
                              <span className="text-slate-500 text-[10px] uppercase font-bold leading-tight truncate mt-0.5">{depts.join(' | ')}</span>
                            ) : null;
                          })()}
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div >
      </div >

      {/* Active Filter Tags */}
      {
        (selectedHeadIds.length > 0 || selectedAssigneeIds.length > 0) && (
          <div className="mb-6 flex flex-col gap-2">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">Active Filters:</span>
            <div className="flex flex-wrap items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200/80 shadow-sm">
              {selectedHeadIds.map(id => {
                const head = committeeHeads.find((h: any) => h.id === id);
                return head ? (
                  <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-200 shadow-sm">
                    Head: <span className="font-bold">{head.name}</span>
                    {head.managedDepartments?.length > 0 && (
                      <span className="text-emerald-500 font-medium">{head.managedDepartments.join(' | ')}</span>
                    )}
                    <button onClick={() => { setSelectedHeadIds(prev => prev.filter(i => i !== id)); setSelectedAssigneeIds([]); resetPage(); }} className="hover:text-emerald-900 text-emerald-500">&times;</button>
                  </span>
                ) : null;
              })}
              {selectedAssigneeIds.map(id => {
                const member = filteredMembers.find((m: any) => m.id === id);
                return member ? (
                  <span key={id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-full border border-indigo-200 shadow-sm">
                    Member: <span className="font-bold">{member.name}</span>
                    {(() => {
                      const depts = member.managedDepartments?.length > 0
                        ? member.managedDepartments
                        : committeeHeads?.find((h: any) => h.id === member.reportsToId)?.managedDepartments || [];
                      return depts.length > 0 ? (
                        <span className="text-indigo-500 font-medium">{depts.join(' | ')}</span>
                      ) : null;
                    })()}
                    <button onClick={() => { setSelectedAssigneeIds(prev => prev.filter(i => i !== id)); resetPage(); }} className="hover:text-indigo-900 text-indigo-500">&times;</button>
                  </span>
                ) : null;
              })}
              <button
                onClick={() => { setSelectedHeadIds([]); setSelectedAssigneeIds([]); resetPage(); }}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium hover:text-red-500 hover:bg-red-50 bg-red-300 text-black rounded-full cursor-pointer transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        )
      }

      {statsLoading ? (
        <div className="flex items-center justify-center py-6" >
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">

          {/* 1) Total Ideas */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 mr-1">
              <div className="bg-blue-50 p-1.5 rounded-lg text-blue-600 shrink-0">
                <Folder className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">Total Ideas</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.totalIdeas || 0}</p>
          </div>

          {/* 2) Hold */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 mr-1">
              <div className="bg-amber-50 p-1.5 rounded-lg text-amber-600 shrink-0">
                <ShieldAlert className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">Hold</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.infoRequested || 0}</p>
          </div>

          {/* 3) Duplicate */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 mr-1">
              <div className="bg-slate-100 p-1.5 rounded-lg text-slate-600 shrink-0">
                <Copy className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">Duplicate</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.duplicateCount || 0}</p>
          </div>

          {/* 4) Rejected */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 mr-1">
              <div className="bg-red-50 p-1.5 rounded-lg text-red-600 shrink-0">
                <Ban className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">Rejected</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.rejected || 0}</p>
          </div>



          {/* 5) Reviewed */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 mr-1">
              <div className="bg-orange-50 p-1.5 rounded-lg text-orange-600 shrink-0">
                <Clock className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">Reviewed</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.underEvaluation || 0}</p>
          </div>

          {/* 6) Yet to Be Reviewed */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 mr-1">
              <div className="bg-indigo-50 p-1.5 rounded-lg text-indigo-600 shrink-0">
                <Send className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">Yet to Be Reviewed</p>
            </div>
            <p className="text-[15px] font-bold text-slate-800 shrink-0">{globalStats?.kpis?.submitted || 0}</p>
          </div>

          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center space-x-2.5 mb-2">
              <div className="bg-emerald-50 p-1.5 rounded-lg text-emerald-600 shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="Approved by MIS">Approved by MIS</p>
            </div>
            <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.misApprovedCount || 0}</p>
              </div>
              <CountCardHint text="Ideas the MIS team has approved. The idea status stays the same." />
            </div>
          </div>

          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center space-x-2.5 mb-2">
              <div className="bg-rose-50 p-1.5 rounded-lg text-rose-600 shrink-0">
                <Ban className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2" title="Rejected by MIS">Rejected by MIS</p>
            </div>
            <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.misRejectedCount || 0}</p>
              </div>
              <CountCardHint text="Ideas the MIS team has rejected. A rejected idea is stored as a duplicate." />
            </div>
          </div>

          {/* 7) WIP – Nos & Value */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center space-x-2.5 mb-2">
              <div className="bg-cyan-50 p-1.5 rounded-lg text-cyan-600 shrink-0">
                <PlayCircle className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold truncate">WIP</p>
            </div>
            <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.workInProgressCount || 0}</p>
              </div>
              <div className="text-right flex-1 min-w-0 pl-1">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                <AmountDisplay amount={globalStats?.kpis?.wipTotalAmount || 0} textSize="text-[13px]" />
              </div>
            </div>
          </div>

          {/* 8) Pending IC Head Approval – Nos & Value */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center space-x-2.5 mb-2">
              <div className="bg-emerald-50 p-1.5 rounded-lg text-emerald-600 shrink-0">
                <Clock className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Pending IC Head Approval</p>
            </div>
            <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.pendingHeadApproval || 0}</p>
              </div>
              <div className="text-right flex-1 min-w-0 pl-1">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                <AmountDisplay amount={globalStats?.kpis?.pendingHeadApprovalValue || 0} textSize="text-[13px]" />
              </div>
            </div>
          </div>

          {/* 9) Requested EC Head Approval – Nos & Value */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center space-x-2.5 mb-2">
              <div className="bg-fuchsia-50 p-1.5 rounded-lg text-fuchsia-600 shrink-0">
                <ExternalLink className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Requested EC Head Approval</p>
            </div>
            <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.pendingExternalApproval || 0}</p>
              </div>
              <div className="text-right flex-1 min-w-0 pl-1">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                <AmountDisplay amount={globalStats?.kpis?.pendingExternalApprovalValue || 0} textSize="text-[13px]" />
              </div>
            </div>
          </div>

          {/* 10) EC Head Approved – Nos & Value */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center space-x-2.5 mb-2">
              <div className="bg-green-50 p-1.5 rounded-lg text-green-600 shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">EC Head Approved</p>
            </div>
            <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.approved || 0}</p>
              </div>
              <div className="text-right flex-1 min-w-0 pl-1">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                <AmountDisplay amount={globalStats?.kpis?.actualExternalApprovedAmount || 0} textSize="text-[13px]" />
              </div>
            </div>
          </div>

          {showFuturePotentialByEc && (
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center space-x-2.5 mb-2">
              <div className="bg-amber-50 p-1.5 rounded-lg text-amber-600 shrink-0">
                <TrendingUp className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">Future potential by EC</p>
            </div>
            <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                <p className="text-[15px] font-bold text-slate-800 leading-none">
                  {/* OLD: Requested − (Approved + Email Confirmed)
                  {(globalStats?.kpis?.pendingExternalApproval || 0) -
                    ((globalStats?.kpis?.approved || 0) + (globalStats?.kpis?.emailConfirmedCount || 0))}
                  */}
                  {globalStats?.kpis?.futurePotentialByEcCount || 0}
                </p>
              </div>
              <div className="text-right flex-1 min-w-0 pl-1">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                <AmountDisplay
                  amount={
                    /* OLD: Requested − (Approved + Email Confirmed)
                    (globalStats?.kpis?.pendingExternalApprovalValue || 0) -
                    ((globalStats?.kpis?.actualExternalApprovedAmount || 0) +
                      (globalStats?.kpis?.emailConfirmationAnnualSavings || 0))
                    */
                    globalStats?.kpis?.futurePotentialByEcValue || 0
                  }
                  textSize="text-[13px]"
                  forceCrores={true}
                />
              </div>
            </div>
          </div>
          )}

          {/* 11) EC Head Rejected – Nos & Value */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center space-x-2.5 mb-2">
              <div className="bg-rose-50 p-1.5 rounded-lg text-rose-600 shrink-0">
                <XCircle className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">EC Head Rejected</p>
            </div>
            <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.externalRejected || 0}</p>
              </div>
              <div className="text-right flex-1 min-w-0 pl-1">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                <AmountDisplay amount={globalStats?.kpis?.externalRejectedValue || 0} textSize="text-[13px]" />
              </div>
            </div>
          </div>

          {/* 12) EC Head Email Confirmed – Nos & Value */}
          <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
            <div className="flex items-center space-x-2.5 mb-2">
              <div className="bg-indigo-50 p-1.5 rounded-lg text-indigo-600 shrink-0">
                <Mail className="w-3.5 h-3.5" />
              </div>
              <p className="text-xs text-slate-600 font-semibold leading-tight line-clamp-2">EC Head Email Confirmed</p>
            </div>
            <div className="flex justify-between items-end border-t border-slate-100 pt-1.5 mt-auto">
              <div>
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nos</p>
                <p className="text-[15px] font-bold text-slate-800 leading-none">{globalStats?.kpis?.emailConfirmedCount || 0}</p>
              </div>
              <div className="text-right flex-1 min-w-0 pl-1">
                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5 truncate">Value</p>
                <AmountDisplay amount={globalStats?.kpis?.emailConfirmationAnnualSavings || 0} textSize="text-[13px]" />
              </div>
            </div>
          </div>

        </div>
      )}
      <IdeasFilterTable
        queryKeyPrefix="leaderIdeas"
        apiEndpoint="/admin/ideas"
        showExport={true}
        showFuturePotentialByEc={showFuturePotentialByEc}
        allowDelete={false}
        actionButtonLabel="Evaluate"
        includeMisStatuses
        assigneeId={finalAssigneeIds.length > 0 ? finalAssigneeIds.join(',') : undefined}
        baseDepartment={effectiveDepartmentIds || undefined}
      />
    </Layout >
  );
}
